import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ActivityEditor, blankConfig, GRADES, inp } from "@/pages/lessons/ActivityEditor";
import LessonPreview from "@/pages/lessons/LessonPreview";
import AssignModal from "@/pages/lessons/AssignModal";
import TutorialEditor from "@/pages/lessons/TutorialEditor";
import TierPickerModal, { TIER_BY_ID } from "@/pages/lessons/TierPickerModal";
import LessonFilters, { EMPTY_FILTERS, filterLessons } from "@/pages/lessons/LessonFilters";
import {
  Plus, Trash2, Save, Sparkles, Loader2, Wand2, Eye, Pencil, BookOpen,
  Library, X, Lock, Crown, Send, Printer, ClipboardList, CheckCircle2, Circle, Undo2, Layers, StopCircle,
} from "lucide-react";


export default function LessonStudio() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [tab, setTab] = useState("build");
  const [mine, setMine] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [preview, setPreview] = useState(null);
  const [assignTarget, setAssignTarget] = useState(null);
  // catalog filters
  const [catFilters, setCatFilters] = useState(EMPTY_FILTERS);
  const [mineFilters, setMineFilters] = useState(EMPTY_FILTERS);

  // form state
  const [editingId, setEditingId] = useState(null);
  const [subject, setSubject] = useState("");
  const [grade, setGrade] = useState("Grade 1");
  const [title, setTitle] = useState("");
  const [tag, setTag] = useState("Lesson");
  const [free, setFree] = useState(false);
  const [objective, setObjective] = useState("");
  const [activities, setActivities] = useState([]);
  const [tutorial, setTutorial] = useState({ steps: [] });
  const [tutBusy, setTutBusy] = useState(false);
  const [engine, setEngine] = useState("mcq");
  const [saving, setSaving] = useState(false);
  const [domainId, setDomainId] = useState("");   // foreign_language subject picks "spanish" | "french"
  const [selection, setSelection] = useState(() => new Set()); // bulk-select for My Lessons
  const [bulkBusy, setBulkBusy] = useState(false);
  const [diffBusy, setDiffBusy] = useState("");      // lesson id currently being differentiated
  const [tierGroup, setTierGroup] = useState(null);  // {below,on,above} for the tier picker

  // AI state
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiJobId, setAiJobId] = useState(null);
  const [aiElapsed, setAiElapsed] = useState(0);
  const aiPollRef = useRef(null);
  const aiTimerRef = useRef(null);

  const isAdmin = user?.role === "admin";
  const access = meta?.has_full_access;
  const tabs = [
    { id: "build", label: "Prompt a Lesson", Icon: Wand2 },
    { id: "mine", label: "My Lessons", Icon: BookOpen },
    { id: "assignments", label: "Assignments", Icon: ClipboardList },
    ...(isAdmin ? [{ id: "catalog", label: "All Lessons", Icon: Library }] : []),
  ];
  const subjColor = (id) => meta?.subjects.find((s) => s.id === id)?.color || "#0D9488";

  const loadLists = useCallback(async () => {
    const [m, c, a] = await Promise.all([
      api.myLessons().catch(() => ({ lessons: [] })),
      isAdmin ? api.adminCatalog().catch(() => ({ lessons: [] })) : Promise.resolve({ lessons: [] }),
      api.listLessonAssignments().catch(() => ({ assignments: [] })),
    ]);
    setMine(m.lessons); setCatalog(c.lessons); setAssignments(a.assignments);
  }, [isAdmin]);

  useEffect(() => {
    api.studioMeta().then((m) => { setMeta(m); if (m.subjects[0]) setSubject((s) => s || m.subjects[0].id); });
    loadLists();
  }, [loadLists]);

  // A remediation draft handed over from Class Insights (Teacher home).
  useEffect(() => {
    if (!meta) return;
    const raw = sessionStorage.getItem("zl_pending_draft");
    if (!raw) return;
    sessionStorage.removeItem("zl_pending_draft");
    try {
      const p = JSON.parse(raw);
      setTab("build"); setSubject(p.subject_id); setGrade(p.grade || "Grade 1");
      applyDraft(p.draft);
      setAiPrompt(`Remediation: ${p.skill}`);
      toast.success("Remediation draft loaded — review, then publish and assign.");
    } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta]);

  const resetForm = () => {
    setEditingId(null); setTitle(""); setObjective(""); setActivities([]);
    setTutorial({ steps: [] }); setTag("Lesson"); setFree(false); setDomainId("");
  };

  const addActivity = () => setActivities((a) => [...a, { engine, title: "Activity", prompt: "", config: blankConfig(engine) }]);

  const applyDraft = (d) => {
    setEditingId(null);
    setTitle(d.title || "");
    setObjective(d.objective || "");
    setTag(d.tag || "Lesson");
    setTutorial(d.tutorial && d.tutorial.steps ? d.tutorial : { steps: [] });
    setActivities((d.activities || []).map((a) => ({ engine: a.engine, title: a.title, prompt: a.prompt, config: a.config })));
  };

  const generateTutorial = async () => {
    setTutBusy(true);
    try {
      const { tutorial: t } = await api.aiGenerateTutorial({ subject_id: subject, grade, title, objective });
      setTutorial(t);
      toast.success("Teaching tutorial ready — review and tweak.");
    } catch (e) { toast.error(formatApiError(e)); } finally { setTutBusy(false); }
  };

  const stopAiPolling = () => {
    if (aiPollRef.current) { clearInterval(aiPollRef.current); aiPollRef.current = null; }
    if (aiTimerRef.current) { clearInterval(aiTimerRef.current); aiTimerRef.current = null; }
  };

  useEffect(() => () => stopAiPolling(), []); // cleanup on unmount

  const generate = async () => {
    if (!aiPrompt.trim()) { toast.error("Describe the lesson you want to create."); return; }
    setAiBusy(true);
    setAiElapsed(0);
    try {
      const { job_id } = await api.aiGenerateLesson({ prompt: aiPrompt, subject_id: subject, grade });
      setAiJobId(job_id);
      aiTimerRef.current = setInterval(() => setAiElapsed((s) => s + 1), 1000);
      aiPollRef.current = setInterval(async () => {
        try {
          const j = await api.aiGenerateStatus(job_id);
          if (j.status === "done") {
            stopAiPolling();
            setAiJobId(null); setAiBusy(false);
            applyDraft(j.draft);
            toast.success("Draft ready — review, tweak, then publish.");
          } else if (j.status === "failed") {
            stopAiPolling();
            setAiJobId(null); setAiBusy(false);
            toast.error(j.error || "AI could not build the lesson. Please rephrase and try again.");
          } else if (j.status === "cancelled") {
            stopAiPolling();
            setAiJobId(null); setAiBusy(false);
            toast.info("Lesson generation stopped.");
          }
        } catch (_) { /* transient — keep polling */ }
      }, 1500);
    } catch (e) {
      setAiBusy(false);
      toast.error(formatApiError(e));
    }
  };

  const cancelGenerate = async () => {
    if (!aiJobId) return;
    try {
      await api.aiGenerateCancel(aiJobId);
      stopAiPolling();
      setAiBusy(false); setAiJobId(null);
      toast.info("Lesson generation stopped. Your slot is free.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const editLesson = (l) => {
    setTab("build");
    setEditingId(l.id);
    setSubject(l.subject_id); setGrade(l.grade || "Grade 1"); setTitle(l.title);
    setTag(l.tag || "Lesson"); setFree(!!l.free); setObjective(l.objective || "");
    setDomainId(l.domain_id || "");
    setTutorial(l.tutorial && l.tutorial.steps ? l.tutorial : { steps: [] });
    setActivities((l.activities || []).map((a) => ({ engine: a.engine, title: a.title, prompt: a.prompt, config: a.config })));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const editById = async (id) => {
    try {
      const l = await api.getLesson(id);
      setTab("build");
      setEditingId(id);
      setSubject(l.subject.id); setGrade(l.grade || "Grade 1"); setTitle(l.title);
      setTag(l.tag || "Lesson"); setFree(!!l.free); setObjective(l.objective || "");
      setDomainId(l.domain_id || "");
      setTutorial(l.tutorial && l.tutorial.steps ? l.tutorial : { steps: [] });
      setActivities((l.activities || []).map((a) => ({ engine: a.engine, title: a.title, prompt: a.prompt, config: a.config })));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cloneAndEdit = async (l) => {
    if (!window.confirm(`"${l.title}" is a built-in lesson. Fork an editable copy? The built-in will be hidden platform-wide.`)) return;
    try {
      const { id } = await api.cloneBuiltinLesson(l.id);
      toast.success("Forked — you can now edit this lesson.");
      await loadLists();
      await editById(id);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const revertFork = async (l) => {
    if (!window.confirm(`Delete this forked copy of "${l.title}" and restore the original built-in lesson? Your edits will be lost.`)) return;
    try {
      await api.revertForkedLesson(l.id);
      toast.success("Reverted to the built-in lesson");
      await loadLists();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async () => {
    if (!title.trim() || activities.length === 0) { toast.error("Add a title and at least one activity."); return; }
    setSaving(true);
    try {
      const body = { subject_id: subject, grade, title, tag, free, objective, skills: [], activities, tutorial,
                     domain_id: subject === "foreign_language" ? (domainId || null) : null };
      if (editingId) { await api.updateMyLesson(editingId, body); toast.success("Lesson updated!"); }
      else { await api.createMyLesson(body); toast.success("Lesson published!"); }
      resetForm(); loadLists(); setTab("mine");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this lesson?")) return;
    try { await api.deleteMyLesson(id); toast.success("Deleted"); loadLists(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const unassign = async (id) => {
    if (!window.confirm("Remove this assignment? Students will no longer see it on their home screen.")) return;
    try { await api.deleteLessonAssignment(id); toast.success("Assignment removed"); loadLists(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  // Differentiation: existing variants are grouped client-side from the lists we already have.
  const allKnown = [...mine, ...catalog];
  const variantsFor = (id) => {
    const groupId = allKnown.find((x) => x.id === id)?.variant_group || id;
    const below = allKnown.find((x) => x.variant_group === groupId && x.variant_level === "below");
    const above = allKnown.find((x) => x.variant_group === groupId && x.variant_level === "above");
    return below && above ? { below, above } : null;
  };
  const toTier = (l) => ({ id: l.id, title: l.title, activities: typeof l.activities === "number" ? l.activities : (l.activities?.length || 0), subject_id: l.subject_id });

  const differentiate = async (l) => {
    if (!access) { navigate("/pricing"); return; }
    const have = variantsFor(l.id);
    if (have) {
      const on = allKnown.find((x) => x.id === (l.variant_group || l.id)) || l;
      setTierGroup({ below: toTier(have.below), on: toTier(on), above: toTier(have.above) });
      return;
    }
    setDiffBusy(l.id);
    try {
      const { tiers, existing } = await api.differentiateLesson(l.id);
      setTierGroup(tiers);
      toast.success(existing ? "This lesson already has tiers." : "Below and Above grade versions are ready.");
      loadLists();
    } catch (e) { toast.error(formatApiError(e)); } finally { setDiffBusy(""); }
  };

  const platformToggle = async (l) => {
    const next = !l.platform_enabled;
    setCatalog((cs) => cs.map((x) => x.id === l.id ? { ...x, platform_enabled: next } : x));
    try {
      await api.platformToggleLesson(l.id, next);
      toast.success(next ? `${l.title} is live platform-wide` : `${l.title} is now hidden from everyone`);
    } catch (e) {
      toast.error(formatApiError(e));
      setCatalog((cs) => cs.map((x) => x.id === l.id ? { ...x, platform_enabled: !next } : x));
    }
  };

  const filteredCatalog = filterLessons(catalog, catFilters);
  const filteredMine = filterLessons(mine, mineFilters);

  if (!meta) return <div className="p-16 text-center"><Loader2 className="mx-auto animate-spin text-[#0D9488]" /></div>;

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
      <div className="text-xs uppercase tracking-widest font-bold text-[#7A3FF2]">{isAdmin ? "Platform Admin" : "Lesson Studio"}</div>
      <h1 className="mt-1 font-heading text-3xl font-extrabold text-[#1F2937]">Lesson Studio</h1>
      <p className="mt-1 font-body text-[#6B7280]">
        Describe a lesson in plain words and let AI build it — or craft one by hand. It appears instantly for your students.
      </p>

      {/* tabs */}
      <div className="mt-6 flex flex-wrap gap-2" data-testid="studio-tabs">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`studio-tab-${t.id}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition-colors ${tab === t.id ? "bg-[#0D9488] text-white" : "border border-[#E5DECF] bg-white text-[#6B7280] hover:bg-[#F2ECE1]"}`}>
            <t.Icon size={15} /> {t.label}
          </button>
        ))}
      </div>

      {/* ---------- BUILD ---------- */}
      {tab === "build" && (
        !access ? <UpgradeGate onSeePlans={() => navigate("/pricing")} /> : (
          <div className="mt-5 space-y-5">
            {/* AI prompt panel */}
            <div className="rounded-3xl border border-[#7A3FF2]/25 bg-gradient-to-br from-[#7A3FF2]/8 to-[#0D9488]/8 p-6" data-testid="ai-panel">
              <div className="flex items-center gap-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#7A3FF2] text-white"><Sparkles size={18} /></div>
                <div>
                  <div className="font-heading text-lg font-extrabold text-[#1F2937]">Prompt a New Lesson</div>
                  <div className="text-xs font-semibold text-[#7A3FF2]">AI Lessons · powered by AI</div>
                </div>
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <label className="text-sm font-bold">Subject
                  <select value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="ai-subject" className={inp}>
                    {meta.subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </label>
                <label className="text-sm font-bold">Grade
                  <select value={grade} onChange={(e) => setGrade(e.target.value)} data-testid="ai-grade" className={inp}>
                    {GRADES.map((g) => <option key={g}>{g}</option>)}
                  </select>
                </label>
              </div>
              <textarea value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} data-testid="ai-prompt"
                placeholder='e.g. "A playful lesson on adding numbers to 10 with apples, and one matching game"'
                className={`${inp} mt-3 h-24 resize-none`} />
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button onClick={generate} disabled={aiBusy} data-testid="ai-generate-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-[#7A3FF2] px-6 py-3 font-heading font-bold text-white hover:bg-[#652BDB] disabled:opacity-50">
                  {aiBusy ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />} {aiBusy ? "Building your lesson…" : "Generate lesson"}
                </button>
                {aiBusy && (
                  <>
                    <span data-testid="ai-elapsed" className="inline-flex items-center rounded-full bg-[#F2ECE1] px-3 py-1 text-xs font-bold text-[#6B7280]">
                      {aiElapsed}s elapsed · typically 20–40s
                    </span>
                    <button onClick={cancelGenerate} data-testid="ai-cancel-btn"
                      className="inline-flex items-center gap-1.5 rounded-full bg-[#E05A47] px-3 py-2 text-xs font-bold text-white hover:brightness-95">
                      <StopCircle size={13} /> Stop
                    </button>
                  </>
                )}
              </div>
              {aiBusy && (
                <p className="mt-2 text-xs text-[#6B7280]">
                  You can safely leave this page — your lesson will keep building in the background and appear on your dashboard.
                </p>
              )}
            </div>

            {/* Manual / review form */}
            <div className="rounded-3xl border border-[#E5DECF] bg-white p-6">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-lg font-extrabold text-[#1F2937]">{editingId ? "Edit lesson" : "Lesson details"}</h2>
                {editingId && <button onClick={resetForm} className="text-sm font-bold text-[#E05A47]">Cancel edit</button>}
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                <label className="text-sm font-bold">Subject<select value={subject} onChange={(e) => { setSubject(e.target.value); if (e.target.value !== "foreign_language") setDomainId(""); }} data-testid="lb-subject" className={inp}>{meta.subjects.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></label>
                <label className="text-sm font-bold">Grade<select value={grade} onChange={(e) => setGrade(e.target.value)} data-testid="lb-grade" className={inp}>{GRADES.map((g) => <option key={g}>{g}</option>)}</select></label>
                <label className="text-sm font-bold">Tag<input value={tag} onChange={(e) => setTag(e.target.value)} className={inp} /></label>
              </div>
              {subject === "foreign_language" && (
                <label className="mt-3 block text-sm font-bold text-[#DB2777]" data-testid="lb-language-wrap">
                  Language
                  <select value={domainId} onChange={(e) => setDomainId(e.target.value)} data-testid="lb-language" className={inp}>
                    <option value="">Select a language…</option>
                    <option value="spanish">Spanish (auto-voice: Shimmer)</option>
                    <option value="french">French (auto-voice: Sage)</option>
                  </select>
                  <span className="mt-1 block text-xs font-normal text-[#94A3B8]">Picks a natural-sounding voice for that language when reading the lesson aloud.</span>
                </label>
              )}
              <label className="mt-3 block text-sm font-bold">Lesson title<input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="lb-title" className={inp} placeholder="e.g. Adding to 10" /></label>
              <label className="mt-3 block text-sm font-bold">Objective<input value={objective} onChange={(e) => setObjective(e.target.value)} className={inp} placeholder="What will the child learn?" /></label>
              {isAdmin && <label className="mt-3 flex items-center gap-2 text-sm font-bold"><input type="checkbox" checked={free} onChange={(e) => setFree(e.target.checked)} data-testid="lb-free" /> Free (no subscription needed)</label>}

              {/* Teach (tutorial) */}
              <div className="mt-6 rounded-2xl border border-[#17BEBB]/30 bg-[#17BEBB]/5 p-4" data-testid="teach-section">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-1.5 font-heading text-base font-extrabold text-[#0B7F74]"><BookOpen size={17} /> Teach (the "Learn" part)</div>
                    <div className="font-body text-xs text-[#6B7280]">Zoodle guides the student through the concept before they practice.</div>
                  </div>
                  <button onClick={generateTutorial} disabled={tutBusy} data-testid="ai-tutorial-btn" className="inline-flex items-center gap-1.5 rounded-full bg-[#17BEBB] px-4 py-2 text-sm font-heading font-bold text-white hover:bg-[#13A3A0] disabled:opacity-50">
                    {tutBusy ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} {tutBusy ? "Writing…" : "Generate tutorial"}
                  </button>
                </div>
                <div className="mt-3">
                  <TutorialEditor
                    tutorial={tutorial}
                    onChange={setTutorial}
                    illustrateCtx={`${meta.subjects.find((s) => s.id === subject)?.name || ""} ${grade} lesson: ${title}`}
                    defaultVoice={
                      subject === "foreign_language"
                        ? (domainId === "spanish" ? { provider: "openai", id: "shimmer", label: "Shimmer (Spanish)" }
                           : domainId === "french" ? { provider: "openai", id: "sage", label: "Sage (French)" }
                           : null)
                        : null
                    }
                  />
                </div>
              </div>

              <div className="mt-6 font-heading text-base font-extrabold text-[#1F2937] flex items-center gap-1.5"><Pencil size={16} /> Practice activities</div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <select value={engine} onChange={(e) => setEngine(e.target.value)} data-testid="lb-engine" className="rounded-xl border-2 border-[#E5DECF] px-3 py-2 font-body">{meta.engines.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}</select>
                <button onClick={addActivity} data-testid="lb-add-activity" className="inline-flex items-center gap-1.5 rounded-full bg-[#10B981] px-4 py-2 font-heading font-bold text-white hover:bg-[#059669]"><Plus size={16} /> Add activity</button>
                {activities.length > 0 && <button onClick={() => setPreview({ draft: { title: title || "Preview", activities }, color: subjColor(subject) })} data-testid="preview-draft-btn" className="inline-flex items-center gap-1.5 rounded-full border border-[#E5DECF] bg-white px-4 py-2 font-heading font-bold text-[#1F2937] hover:bg-[#F2ECE1]"><Eye size={16} /> Preview</button>}
              </div>

              <div className="mt-4 space-y-3">
                {activities.map((a, i) => (
                  <ActivityEditor key={i} act={a} idx={i} onChange={(na) => setActivities((arr) => arr.map((x, j) => (j === i ? na : x)))} onRemove={() => setActivities((arr) => arr.filter((_, j) => j !== i))} />
                ))}
                {activities.length === 0 && <p className="font-body text-sm text-[#94A3B8]">No activities yet — generate with AI above, or pick an engine and add one.</p>}
              </div>

              <button onClick={save} disabled={saving} data-testid="lb-publish" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#0D9488] px-6 py-3 font-heading font-bold text-white hover:bg-[#0B7F74] disabled:opacity-50">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} {editingId ? "Save changes" : "Publish lesson"}
              </button>
            </div>
          </div>
        )
      )}

      {/* ---------- MY LESSONS ---------- */}
      {tab === "mine" && (
        <div className="mt-5 space-y-2" data-testid="my-lessons-list">
          {mine.length > 0 && (
            <LessonFilters rows={mine} subjects={meta.subjects} value={mineFilters} onChange={setMineFilters} prefix="mine" total={mine.length} shown={filteredMine.length} />
          )}
          {mine.length > 0 && (
            <BulkToolbar
              testId="my-lessons-bulk-toolbar"
              eligibleIds={filteredMine.map((l) => l.id)}
              selection={selection}
              setSelection={setSelection}
              bulkBusy={bulkBusy}
              setBulkBusy={setBulkBusy}
              reload={loadLists}
            />
          )}
          {filteredMine.length === 0 && mine.length > 0 && <p className="py-8 text-center font-body text-sm text-[#94A3B8]" data-testid="mine-no-matches">No lessons match your filters.</p>}
          {filteredMine.map((l) => (
            <div key={l.id} className="flex items-start gap-2">
              <label className="mt-3 inline-flex cursor-pointer items-center rounded-xl bg-white/80 p-2 shadow-sm hover:bg-[#F2ECE1]" data-testid={`my-select-${l.id}`}>
                <input
                  type="checkbox"
                  checked={selection.has(l.id)}
                  onChange={() => toggleSelection(setSelection, l.id)}
                  className="h-4 w-4 rounded border-[#E5DECF] text-[#0D9488]"
                />
              </label>
              <div className="flex-1">
                <LessonRow l={l} color={subjColor(l.subject_id)} subjectName={meta.subjects.find((s) => s.id === l.subject_id)?.name}
                  onPreview={() => setPreview({ lessonId: l.id, color: subjColor(l.subject_id) })}
                  onEdit={() => editLesson(l)} onDelete={() => del(l.id)}
                  onAssign={() => setAssignTarget({ id: l.id, title: l.title })}
                  onDifferentiate={() => differentiate(l)} diffBusy={diffBusy === l.id} hasTiers={!!variantsFor(l.id)}
                  onWorksheet={() => navigate(`/worksheet/${l.id}`)} editable />
              </div>
            </div>
          ))}
          {mine.length === 0 && (
            <div className="rounded-3xl border border-dashed border-[#E5DECF] bg-white p-12 text-center">
              <BookOpen size={38} className="mx-auto text-[#94A3B8]" />
              <div className="mt-3 font-heading text-lg font-bold text-[#6B7280]">No lessons yet</div>
              <p className="mt-1 font-body text-[#94A3B8]">Head to “Prompt a Lesson” to create your first one.</p>
              <button onClick={() => setTab("build")} className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#7A3FF2] px-5 py-2.5 font-bold text-white"><Wand2 size={16} /> Prompt a Lesson</button>
            </div>
          )}
        </div>
      )}

      {/* ---------- ASSIGNMENTS (tracking) ---------- */}
      {tab === "assignments" && (
        <div className="mt-5 space-y-3" data-testid="assignments-list">
          <p className="font-body text-sm text-[#6B7280]">See who has finished each lesson you assigned and who still has it to do.</p>
          {assignments.map((a) => <AssignmentCard key={a.id} a={a} onOpenPreview={() => setPreview({ lessonId: a.lesson_id, color: a.subject?.color || "#0D9488" })} onUnassign={() => unassign(a.id)} />)}
          {assignments.length === 0 && (
            <div className="rounded-3xl border border-dashed border-[#E5DECF] bg-white p-12 text-center">
              <ClipboardList size={38} className="mx-auto text-[#94A3B8]" />
              <div className="mt-3 font-heading text-lg font-bold text-[#6B7280]">No assignments yet</div>
              <p className="mt-1 font-body text-[#94A3B8]">Use the “Assign” button on any lesson to send it to a student.</p>
            </div>
          )}
        </div>
      )}

      {/* ---------- CATALOG (admin) ---------- */}
      {tab === "catalog" && isAdmin && (
        <div className="mt-5" data-testid="catalog-list">
          <LessonFilters rows={catalog} subjects={meta.subjects} value={catFilters} onChange={setCatFilters} prefix="catalog" total={catalog.length} shown={filteredCatalog.length} />
          <p className="mt-3 font-body text-xs text-[#6B7280]" data-testid="catalog-hint">Toggle a lesson off to hide it platform-wide from every teacher & student.</p>
          {(() => {
            const eligibleIds = filteredCatalog.filter((l) => l.editable).map((l) => l.id);
            return eligibleIds.length > 0 ? (
              <div className="mt-2">
                <BulkToolbar
                  testId="catalog-bulk-toolbar"
                  eligibleIds={eligibleIds}
                  selection={selection}
                  setSelection={setSelection}
                  bulkBusy={bulkBusy}
                  setBulkBusy={setBulkBusy}
                  reload={loadLists}
                  hint="Bulk actions apply to custom lessons only. Built-in lessons don't have Free/Active toggles."
                />
              </div>
            ) : null;
          })()}
          <div className="mt-2 space-y-2">
            {filteredCatalog.map((l) => (
              <div key={l.id} className="flex items-start gap-2">
                {l.editable ? (
                  <label className="mt-3 inline-flex cursor-pointer items-center rounded-xl bg-white/80 p-2 shadow-sm hover:bg-[#F2ECE1]" data-testid={`catalog-select-${l.id}`}>
                    <input
                      type="checkbox"
                      checked={selection.has(l.id)}
                      onChange={() => toggleSelection(setSelection, l.id)}
                      className="h-4 w-4 rounded border-[#E5DECF] text-[#0D9488]"
                    />
                  </label>
                ) : (
                  <span className="mt-3 inline-flex w-9 items-center justify-center p-2" aria-hidden="true" />
                )}
                <div className="flex-1">
                  <LessonRow l={{ ...l, activities: l.activities }} count={l.activities} color={l.subject_color} subjectName={l.subject_name}
                    source={l.source}
                    platformEnabled={l.platform_enabled}
                    onPlatformToggle={() => platformToggle(l)}
                    onPreview={() => setPreview({ lessonId: l.id, color: l.subject_color })}
                    onEdit={l.editable ? () => editById(l.id) : () => cloneAndEdit(l)}
                    onDelete={l.editable ? () => del(l.id) : null}
                    onRevertFork={l.cloned_from ? () => revertFork(l) : null}
                    onAssign={() => setAssignTarget({ id: l.id, title: l.title })}
                    onDifferentiate={() => differentiate(l)} diffBusy={diffBusy === l.id} hasTiers={!!variantsFor(l.id)}
                    onWorksheet={() => navigate(`/worksheet/${l.id}`)}
                    editable={l.editable} />
                </div>
              </div>
            ))}
            {filteredCatalog.length === 0 && <p className="py-8 text-center font-body text-sm text-[#94A3B8]">No lessons match your filters.</p>}
          </div>
        </div>
      )}

      <LessonPreview open={!!preview} onClose={() => setPreview(null)} lessonId={preview?.lessonId} draft={preview?.draft} color={preview?.color} />
      <AssignModal lesson={assignTarget} onClose={() => setAssignTarget(null)} onDone={loadLists} />
      <TierPickerModal group={tierGroup} onClose={() => setTierGroup(null)}
        onPreview={(t) => { setTierGroup(null); setPreview({ lessonId: t.id, color: subjColor(t.subject_id) }); }}
        onAssign={(t) => { setTierGroup(null); setAssignTarget({ id: t.id, title: t.title }); }} />
    </div>
  );
}

function toggleSelection(setSelection, id) {
  setSelection((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
}

function BulkToolbar({ testId, eligibleIds, selection, setSelection, bulkBusy, setBulkBusy, reload, hint }) {
  const eligibleSet = React.useMemo(() => new Set(eligibleIds), [eligibleIds]);
  const selectedInScope = React.useMemo(
    () => Array.from(selection).filter((id) => eligibleSet.has(id)),
    [selection, eligibleSet],
  );
  const selCount = selectedInScope.length;
  const allSelected = eligibleIds.length > 0 && selCount === eligibleIds.length;
  const someSelected = selCount > 0 && !allSelected;
  const toggleAll = () => {
    if (allSelected) {
      setSelection((prev) => {
        const next = new Set(prev);
        eligibleIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelection((prev) => {
        const next = new Set(prev);
        eligibleIds.forEach((id) => next.add(id));
        return next;
      });
    }
  };
  const applyPatch = async (patch, label) => {
    if (!selCount) return;
    setBulkBusy(true);
    try {
      const r = await api.bulkUpdateMyLessons({ ids: selectedInScope, ...patch });
      toast.success(`${r.modified} lesson${r.modified === 1 ? "" : "s"} updated (${label})`);
      setSelection((prev) => {
        const next = new Set(prev);
        selectedInScope.forEach((id) => next.delete(id));
        return next;
      });
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBulkBusy(false); }
  };
  return (
    <div className="sticky top-0 z-10 mb-2 flex flex-wrap items-center gap-2 rounded-2xl border border-[#E5DECF] bg-white/95 px-3 py-2 shadow-sm backdrop-blur-sm" data-testid={testId}>
      <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl px-2 py-1 text-sm font-bold text-[#1F2937] hover:bg-[#F2ECE1]">
        <input
          type="checkbox"
          checked={allSelected}
          ref={(el) => { if (el) el.indeterminate = someSelected; }}
          onChange={toggleAll}
          data-testid={`${testId}-select-all`}
          className="h-4 w-4 rounded border-[#E5DECF] text-[#0D9488]"
        />
        {selCount > 0 ? `${selCount} selected` : "Select all"}
      </label>
      {selCount > 0 && (
        <>
          <span className="hidden text-[10px] font-bold uppercase tracking-widest text-[#94A3B8] sm:inline">Bulk update:</span>
          <button disabled={bulkBusy} onClick={() => applyPatch({ free: true }, "free")} data-testid="bulk-make-free" className="rounded-full bg-[#10B981]/15 px-3 py-1 text-xs font-bold text-[#047857] hover:bg-[#10B981]/25 disabled:opacity-50">Make free</button>
          <button disabled={bulkBusy} onClick={() => applyPatch({ free: false }, "not free")} data-testid="bulk-make-paid" className="rounded-full bg-[#F59E0B]/15 px-3 py-1 text-xs font-bold text-[#B45309] hover:bg-[#F59E0B]/25 disabled:opacity-50">Not free</button>
          <button disabled={bulkBusy} onClick={() => applyPatch({ active: true }, "active")} data-testid="bulk-make-active" className="rounded-full bg-[#0D9488]/15 px-3 py-1 text-xs font-bold text-[#0B7F74] hover:bg-[#0D9488]/25 disabled:opacity-50">Set active</button>
          <button disabled={bulkBusy} onClick={() => applyPatch({ active: false }, "inactive")} data-testid="bulk-make-inactive" className="rounded-full bg-[#94A3B8]/15 px-3 py-1 text-xs font-bold text-[#475569] hover:bg-[#94A3B8]/25 disabled:opacity-50">Set inactive</button>
          <button onClick={() => setSelection((prev) => { const next = new Set(prev); selectedInScope.forEach((id) => next.delete(id)); return next; })} data-testid="bulk-clear" className="ml-auto rounded-full px-3 py-1 text-xs font-bold text-[#6B7280] hover:bg-[#F2ECE1]">Cancel</button>
          {bulkBusy && <Loader2 size={14} className="animate-spin text-[#0D9488]" />}
        </>
      )}
      {hint && selCount === 0 && (
        <span className="ml-auto text-[11px] font-body text-[#94A3B8]">{hint}</span>
      )}
    </div>
  );
}

function LessonRow({ l, color, subjectName, source, count, onPreview, onEdit, onDelete, onAssign, onWorksheet, editable, onPlatformToggle, platformEnabled, onRevertFork, onDifferentiate, diffBusy, hasTiers }) {
  const activitiesCount = typeof count === "number" ? count : (l.activities?.length || 0);
  const off = onPlatformToggle && platformEnabled === false;
  const inactive = l.active === false;
  const tier = l.variant_level && l.variant_level !== "on" ? TIER_BY_ID[l.variant_level] : null;
  return (
    <div className={`flex flex-wrap items-center justify-between gap-3 rounded-2xl border bg-white px-4 py-3 ${off ? "border-dashed border-[#E05A47]/40 opacity-70" : inactive ? "border-dashed border-[#94A3B8]/40 opacity-70" : "border-[#E5DECF]"}`} data-testid={`lesson-row-${l.id}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />
          <span className="font-heading font-bold text-[#1F2937]">{l.title}</span>
          {tier && <span className="inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[10px] font-bold text-white" style={{ background: tier.color }} data-testid={`tier-pill-${l.id}`}><tier.Icon size={10} strokeWidth={3} /> {tier.label}</span>}
          {source && <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${source === "custom" ? "bg-[#10B981]/15 text-[#047857]" : "bg-[#F2ECE1] text-[#6B7280]"}`}>{source === "custom" ? "Custom" : "Built-in"}</span>}
          {l.free && <span className="rounded-full bg-[#F59E0B]/15 px-2 py-0.5 text-[10px] font-bold text-[#B45309]">Free</span>}
          {off && <span className="rounded-full bg-[#E05A47]/10 px-2 py-0.5 text-[10px] font-bold text-[#E05A47]">Hidden</span>}
          {inactive && <span className="rounded-full bg-[#94A3B8]/15 px-2 py-0.5 text-[10px] font-bold text-[#475569]">Inactive</span>}
        </div>
        <div className="mt-0.5 text-xs text-[#6B7280]">{subjectName} · {l.grade} · {activitiesCount} activities</div>
      </div>
      <div className="flex flex-wrap items-center gap-1">
        {onPlatformToggle && (
          <button onClick={onPlatformToggle} data-testid={`platform-toggle-${l.id}`} className={`mr-1 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-xs font-bold ${platformEnabled ? "text-[#0D9488]" : "text-[#94A3B8]"}`} title={platformEnabled ? "Live — click to hide platform-wide" : "Hidden — click to make live"}>
            <span className={`relative h-4 w-8 rounded-full transition-colors ${platformEnabled ? "bg-[#0D9488]" : "bg-[#CBD5E1]"}`}><span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all ${platformEnabled ? "left-[18px]" : "left-0.5"}`} /></span>
            {platformEnabled ? "Live" : "Off"}
          </button>
        )}
        {onAssign && <button onClick={onAssign} data-testid={`assign-${l.id}`} className="inline-flex items-center gap-1 rounded-xl px-2.5 py-1.5 text-xs font-bold text-[#047857] hover:bg-[#10B981]/10"><Send size={14} /> Assign</button>}
        {onDifferentiate && !tier && (
          <button onClick={onDifferentiate} disabled={diffBusy} data-testid={`differentiate-${l.id}`} title="Create Below / On / Above grade versions with the same objective"
            className="inline-flex items-center gap-1 rounded-xl px-2.5 py-1.5 text-xs font-bold text-[#7A3FF2] hover:bg-[#7A3FF2]/10 disabled:opacity-60">
            {diffBusy ? <Loader2 size={14} className="animate-spin" /> : <Layers size={14} />} {diffBusy ? "Differentiating…" : hasTiers ? "Tiers" : "Differentiate"}
          </button>
        )}
        {onWorksheet && <button onClick={onWorksheet} data-testid={`worksheet-${l.id}`} className="inline-flex items-center gap-1 rounded-xl px-2.5 py-1.5 text-xs font-bold text-[#B45309] hover:bg-[#F59E0B]/10"><Printer size={14} /> Worksheet</button>}
        <button onClick={onPreview} data-testid={`preview-${l.id}`} className="inline-flex items-center gap-1 rounded-xl px-2.5 py-1.5 text-xs font-bold text-[#0D9488] hover:bg-[#0D9488]/10"><Eye size={14} /> Preview</button>
        {onEdit && <button onClick={onEdit} data-testid={`edit-${l.id}`} className="inline-flex items-center gap-1 rounded-xl px-2.5 py-1.5 text-xs font-bold text-[#6B7280] hover:bg-[#F2ECE1]"><Pencil size={14} /> {editable ? "Edit" : "Fork & Edit"}</button>}
        {onRevertFork && <button onClick={onRevertFork} data-testid={`revert-${l.id}`} className="inline-flex items-center gap-1 rounded-xl px-2.5 py-1.5 text-xs font-bold text-[#B45309] hover:bg-[#F59E0B]/10" title="Delete this fork and restore the built-in lesson"><Undo2 size={14} /> Revert to built-in</button>}
        {editable && onDelete && <button onClick={onDelete} data-testid={`delete-${l.id}`} className="inline-flex items-center gap-1 rounded-xl px-2.5 py-1.5 text-xs font-bold text-[#E05A47] hover:bg-[#E05A47]/10"><Trash2 size={14} /> Delete</button>}
      </div>
    </div>
  );
}

function AssignmentCard({ a, onOpenPreview, onUnassign }) {
  const [open, setOpen] = useState(false);
  const color = a.subject?.color || "#0D9488";
  const pct = a.total ? Math.round((a.completed_count / a.total) * 100) : 0;
  return (
    <div className="rounded-2xl border border-[#E5DECF] bg-white p-4" data-testid={`assignment-${a.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />
            <span className="font-heading font-bold text-[#1F2937]">{a.title}</span>
            {a.missing && <span className="rounded-full bg-[#E05A47]/15 px-2 py-0.5 text-[10px] font-bold text-[#E05A47]">Lesson removed</span>}
          </div>
          <div className="mt-0.5 text-xs text-[#6B7280]">{a.subject?.name}{a.note ? ` · “${a.note}”` : ""}</div>
        </div>
        <div className="flex items-center gap-2">
          <span className="rounded-full px-3 py-1 text-xs font-bold" style={{ background: `${color}1A`, color }} data-testid={`assignment-progress-${a.id}`}>{a.completed_count}/{a.total} done</span>
          <button onClick={onOpenPreview} className="inline-flex items-center gap-1 rounded-xl px-2.5 py-1.5 text-xs font-bold text-[#0D9488] hover:bg-[#0D9488]/10"><Eye size={14} /> Preview</button>
          <button onClick={onUnassign} data-testid={`unassign-${a.id}`} className="inline-flex items-center gap-1 rounded-xl px-2.5 py-1.5 text-xs font-bold text-[#E05A47] hover:bg-[#E05A47]/10"><Trash2 size={14} /> Remove</button>
        </div>
      </div>
      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-[#F2ECE1]"><div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} /></div>
      <button onClick={() => setOpen((o) => !o)} data-testid={`assignment-toggle-${a.id}`} className="mt-3 text-xs font-bold text-[#6B7280] hover:text-[#0D9488]">
        {open ? "Hide" : "Show"} students ({a.total})
      </button>
      {open && (
        <div className="mt-2 grid gap-1.5 sm:grid-cols-2" data-testid={`assignment-students-${a.id}`}>
          {a.students.map((s) => (
            <div key={s.id} className="flex items-center justify-between rounded-xl bg-[#FAF7F2] px-3 py-2">
              <span className="font-body text-sm font-semibold text-[#1F2937]">{s.name} <span className="text-[#94A3B8]">@{s.username}</span></span>
              {s.completed
                ? <span className="inline-flex items-center gap-1 text-xs font-bold text-[#047857]"><CheckCircle2 size={14} /> Done</span>
                : <span className="inline-flex items-center gap-1 text-xs font-bold text-[#B45309]"><Circle size={14} /> {s.progress_percentage > 0 ? `${s.progress_percentage}%` : "To do"}</span>}
            </div>
          ))}
          {a.students.length === 0 && <p className="text-xs text-[#94A3B8]">No students on this assignment.</p>}
        </div>
      )}
    </div>
  );
}

function UpgradeGate({ onSeePlans }) {
  return (
    <div className="mt-6 overflow-hidden rounded-[28px] border border-[#7A3FF2]/25 bg-gradient-to-br from-[#7A3FF2]/10 to-[#0D9488]/10 p-8 text-center" data-testid="studio-upgrade-gate">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#7A3FF2] text-white shadow-lg"><Lock size={30} /></div>
      <h2 className="mt-5 font-heading text-2xl font-extrabold text-[#1F2937]">Prompt a New Lesson with AI</h2>
      <p className="mx-auto mt-2 max-w-md font-body text-[#6B7280]">
        Describe any lesson in plain words and our AI builds ready-to-play activities for your students in seconds.
        Unlock <b>AI Lessons</b> with any paid plan.
      </p>
      <div className="mt-4 flex flex-wrap justify-center gap-3 text-sm font-semibold text-[#7A3FF2]">
        <span className="inline-flex items-center gap-1.5"><Sparkles size={15} /> Instant AI drafts</span>
        <span className="inline-flex items-center gap-1.5"><Pencil size={15} /> Edit anything</span>
        <span className="inline-flex items-center gap-1.5"><Eye size={15} /> Preview before publishing</span>
      </div>
      <button onClick={onSeePlans} data-testid="gate-see-plans" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#7A3FF2] px-7 py-3.5 font-heading font-bold text-white shadow-md hover:bg-[#652BDB]">
        <Crown size={18} /> Upgrade to unlock
      </button>
    </div>
  );
}
